import {
  Ellipsis,
  GenericTable,
  GenericTableVariant,
  ReturnIcon,
} from "@visiblethread/react-components";
import {
  isSiteBlocked,
  NO_ALLOWED_SITES_MESSAGE,
} from "constants/sharepointAccess";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "utils";

import { selectSelectedCollection } from "../../../../../slices/collections/selectors";
import { selectSharepoint } from "../../../../../slices/sharepoint/selectors";
import {
  resetSharepointSearch,
  setPath,
} from "../../../../../slices/sharepoint/sharepointSlice";
import {
  createSharepointNavigationPath,
  fetchSharepointData,
} from "../helpers";
import ActionsCell from "./FilesExplorerTableCells/ActionsCell";
import NameCell from "./FilesExplorerTableCells/NameCell";
import TagsCell from "./FilesExplorerTableCells/TagsCell";
import SelectAllCheckbox from "./SelectAllCheckbox";
import classes from "./SharePointFileExplorer.module.scss";

const NO_CONTENT = "No Files Found.\n\nThere are no files in this folder.";
const LAST_UPDATED_COLUMN_SIZE = 104;
const LOCATION_COLUMN_SIZE = 170;

export const getColumns = ({
  data,
  isRoot,
  selectedFiles,
  appliedFiles,
  isAllFilesApplied,
  files,
  loading,
  isAllFilesChecked,
  isSearch,
  handleSelectAllFiles,
  handleBackClick,
}) => {
  const columns = [
    {
      header:
        isRoot || isSearch ? (
          <span>{isSearch ? `Results (${data.length})` : "Top Level"}</span>
        ) : (
          <button className="file_explorer__header" onClick={handleBackClick}>
            <ReturnIcon />
            <span style={{ cursor: "pointer" }}>Back</span>
          </button>
        ),
      accessorKey: "name",
      size: isSearch ? 180 : 350,
      minSize: isSearch ? 180 : 350,
      enableSorting: isSearch,
      meta: { flexGrow: isSearch ? 0 : 1 },
      cell: ({ row }) => {
        return (
          <NameCell
            row={row}
            selectedFiles={selectedFiles}
            appliedFiles={appliedFiles}
          />
        );
      },
      sortingFn: (a, b) =>
        a.original.name.localeCompare(b.original.name, "en", {
          numeric: true,
          sensitivity: "base",
        }),
    },
  ];

  if (isSearch) {
    columns.push({
      header: "Last Updated",
      accessorKey: "lastModified",
      size: LAST_UPDATED_COLUMN_SIZE,
      minSize: LAST_UPDATED_COLUMN_SIZE,
      enableSorting: true,
      meta: { flexGrow: 0 },
      cell: ({ row }) => {
        const lastModified = row.original.lastModified;
        const date = lastModified ? formatDate(new Date(lastModified)) : "-";

        return <span className={classes.lastUpdated}>{date}</span>;
      },
      sortingFn: (a, b) => {
        const aTime = a.original.lastModified
          ? new Date(a.original.lastModified).getTime()
          : 0;
        const bTime = b.original.lastModified
          ? new Date(b.original.lastModified).getTime()
          : 0;

        return aTime - bTime;
      },
    });

    columns.push({
      header: "Location",
      accessorKey: "sitePath",
      size: LOCATION_COLUMN_SIZE,
      minSize: LOCATION_COLUMN_SIZE,
      enableSorting: true,
      meta: { flexGrow: 0 },
      cell: ({ getValue }) => {
        const path = getValue() || "";
        const fullPath = path.replace(/^\//i, "").split("/").join(" > ");

        return (
          <span className={classes.location}>
            <Ellipsis
              text={fullPath}
              classes={classes.location__text}
              tooltipDelay={1000}
            />
          </span>
        );
      },
      sortingFn: (a, b) =>
        (a.original.sitePath || "").localeCompare(
          b.original.sitePath || "",
          "en",
          { numeric: true, sensitivity: "base" }
        ),
    });

    columns.push({
      header: "Tags",
      accessorKey: "tags",
      size: 150,
      enableSorting: true,
      meta: { flexGrow: 0 },
      cell: ({ getValue }) => <TagsCell tags={getValue()} />,
      sortingFn: (a, b) => {
        const aTags = a.original.tags || [];
        const bTags = b.original.tags || [];

        if (aTags.length !== bTags.length) {
          return aTags.length - bTags.length;
        }

        return (aTags[0]?.label || "").localeCompare(
          bTags[0]?.label || "",
          "en",
          { sensitivity: "base" }
        );
      },
    });
  }

  columns.push({
    header: (
      <SelectAllCheckbox
        isAllFilesApplied={isAllFilesApplied}
        files={files}
        loading={loading}
        isAllFilesChecked={isAllFilesChecked}
        handleSelectAllFiles={handleSelectAllFiles}
      />
    ),
    id: "actions",
    size: 40,
    minSize: 40,
    enableSorting: false,
    enableResizing: false,
    pin: "right",
    cell: ({ row }) => (
      <ActionsCell
        row={row}
        selectedFiles={selectedFiles}
        appliedFiles={appliedFiles}
        isSite={row.original.type === "site"}
      />
    ),
  });

  return columns;
};

const SharePointFileExplorer = ({
  selectedFiles,
  isLoadingMore = false,
  setSelectedFiles,
  onScroll,
}) => {
  const dispatch = useDispatch();

  const {
    data,
    error,
    loading: isFetching,
    search,
    path,
    restricted,
    authPending,
  } = useSelector(selectSharepoint);
  const loading = isFetching || authPending;
  const selectedCollection = useSelector(selectSelectedCollection);
  const files = data.filter(
    (item) => item.type === "file" || item.type === "SP.File"
  );

  const appliedFiles = useMemo(() => {
    return selectedCollection?.files || [];
  }, [selectedCollection?.files]);

  const isAllFilesChecked = useMemo(
    () =>
      files.every((selectableFile) =>
        selectedFiles.some((file) => file.name === selectableFile.name)
      ),
    [files, selectedFiles]
  );

  const isAllFilesApplied = useMemo(
    () =>
      files.every((selectableFile) =>
        appliedFiles.some((file) => file.content.name === selectableFile.name)
      ),
    [files, appliedFiles]
  );

  const isRoot = path.length <= 1;

  const handleSelectAllFiles = useCallback(() => {
    isAllFilesChecked ? setSelectedFiles([]) : setSelectedFiles(files);
  }, [isAllFilesChecked, files, setSelectedFiles]);

  const handleBackClick = useCallback(() => {
    dispatch(setPath(path.slice(0, -1)));
    fetchSharepointData(dispatch, path[path.length - 2]);
  }, [dispatch, path]);

  const handleSelectFile = useCallback(
    (file, isChecked) => {
      isChecked
        ? setSelectedFiles((previousFiles) =>
            previousFiles.filter(
              (previousFile) => previousFile.name !== file.name
            )
          )
        : setSelectedFiles((previousFiles) => [...previousFiles, file]);
    },
    [setSelectedFiles]
  );
  const columns = useMemo(() => {
    return getColumns({
      data,
      isRoot,
      selectedFiles,
      appliedFiles,
      isAllFilesApplied,
      files,
      loading,
      isAllFilesChecked,
      isSearch: !!search.isSearching,
      handleSelectAllFiles,
      handleBackClick,
    });
  }, [
    data,
    appliedFiles,
    files,
    handleBackClick,
    handleSelectAllFiles,
    isAllFilesApplied,
    isAllFilesChecked,
    search,
    isRoot,
    loading,
    selectedFiles,
  ]);

  const handleRowClick = (row) => {
    if (isSiteBlocked(row, restricted)) {
      return;
    }
    // Handle folders/non-files
    if (row.type !== "file" && row.type !== "SP.File") {
      fetchSharepointData(dispatch, row);
      createSharepointNavigationPath(dispatch, row, setPath);
      dispatch(resetSharepointSearch());
      return;
    }

    // Handle files - only if not already applied
    if (!appliedFiles.some((file) => file.content.id === row.id)) {
      handleSelectFile(
        row,
        selectedFiles.some((file) => file.name === row.name)
      );
    }
  };

  return (
    <div className="file_explorer" data-testid="file-explorer">
      <GenericTable
        key={search.isSearching ? "search" : "explorer"}
        className={classes.table}
        cellClassName={classes.cell}
        data={data}
        columns={columns}
        loading={loading}
        loadingMore={isLoadingMore}
        onRowClick={(row) => handleRowClick(row)}
        emptyMessage={
          <div className="file_explorer__list__empty">
            {error
              ? error.message
              : restricted && isRoot && !search.isSearching
                ? NO_ALLOWED_SITES_MESSAGE
                : NO_CONTENT}
          </div>
        }
        onScroll={onScroll}
        defaultSortColumnIndex={-1}
        variant={GenericTableVariant.bordered}
      />
    </div>
  );
};

const itemShape = shape({
  name: string.isRequired,
});

SharePointFileExplorer.propTypes = {
  selectedFiles: arrayOf(itemShape).isRequired,
  isLoadingMore: bool,
  setSelectedFiles: func.isRequired,
  onScroll: func,
};

export default SharePointFileExplorer;
